import React, { useState } from 'react';
import axios from 'axios';
import { Mail, Phone, MapPin, Send, Calendar, User, MessageSquare } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { mockData } from '../mock';
import { toast } from 'sonner';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    serviceType: '',
    appointmentDate: '',
    message: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, serviceType: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Submit to backend API
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/booking`, formData);
      
      if (response.data.success) {
        toast.success(response.data.message);
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          serviceType: '',
          appointmentDate: '',
          message: ''
        });
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Booking submission error:', error);
      toast.error('Failed to submit booking request. Please call us at (470) 479-2055.');
    }
  };

  return (
    <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8 bg-black">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16 space-y-4">
          <h2 className="text-4xl font-bold text-white">
            Schedule Your Ride Today
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Fill out the form below or give us a call. We're here to help you get to your appointment safely and on time.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Info Cards */}
          <div className="space-y-6">
            <Card className="border-2 border-gray-800 hover:border-cyan-500 transition-colors bg-gray-900">
              <CardContent className="pt-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-gradient-to-br from-cyan-500/20 to-purple-600/20 rounded-xl p-3">
                    <Phone className="w-6 h-6 text-cyan-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">Phone</h3>
                    <a 
                      href={`tel:${mockData.company.phone.replace(/\D/g, '')}`}
                      className="text-cyan-400 hover:text-cyan-300 font-medium"
                    >
                      {mockData.company.phone}
                    </a>
                    <p className="text-sm text-gray-400 mt-1">Available 24/7</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-800 hover:border-cyan-500 transition-colors bg-gray-900">
              <CardContent className="pt-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-gradient-to-br from-cyan-500/20 to-purple-600/20 rounded-xl p-3">
                    <Mail className="w-6 h-6 text-cyan-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">Email</h3>
                    <a 
                      href={`mailto:${mockData.company.email}`}
                      className="text-cyan-400 hover:text-cyan-300 font-medium break-all"
                    >
                      {mockData.company.email}
                    </a>
                    <p className="text-sm text-gray-400 mt-1">We reply within 24 hours</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-800 hover:border-cyan-500 transition-colors bg-gray-900">
              <CardContent className="pt-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-gradient-to-br from-cyan-500/20 to-purple-600/20 rounded-xl p-3">
                    <MapPin className="w-6 h-6 text-cyan-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">Address</h3>
                    <p className="text-gray-300">{mockData.company.address}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Booking Form */}
          <div className="lg:col-span-2">
            <Card className="border-2 border-gray-800 bg-gray-900">
              <CardHeader>
                <CardTitle className="text-2xl text-white">Request a Ride</CardTitle>
                <CardDescription className="text-gray-300">
                  Complete the form and we'll contact you to confirm your transportation
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-gray-300">Full Name *</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-3 w-5 h-5 text-gray-500" />
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="John Doe"
                          className="pl-10 bg-gray-800 border-gray-700 text-white placeholder:text-gray-500"
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-gray-300">Phone Number *</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-3 w-5 h-5 text-gray-500" />
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="(470) 479-2055"
                          className="pl-10 bg-gray-800 border-gray-700 text-white placeholder:text-gray-500"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-gray-300">Email Address *</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 w-5 h-5 text-gray-500" />
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="john@example.com"
                          className="pl-10 bg-gray-800 border-gray-700 text-white placeholder:text-gray-500"
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="serviceType" className="text-gray-300">Service Type *</Label>
                      <Select onValueChange={handleSelectChange} required>
                        <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                          <SelectValue placeholder="Select service type" />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-800 border-gray-700">
                          <SelectItem value="medicaid" className="text-white hover:bg-gray-700">Medicaid Ride</SelectItem>
                          <SelectItem value="private" className="text-white hover:bg-gray-700">Private Pay</SelectItem>
                          <SelectItem value="facility" className="text-white hover:bg-gray-700">Facility Partnership</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="appointmentDate" className="text-gray-300">Appointment Date *</Label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-3 w-5 h-5 text-gray-500" />
                      <Input
                        id="appointmentDate"
                        name="appointmentDate"
                        type="date"
                        value={formData.appointmentDate}
                        onChange={handleInputChange}
                        className="pl-10 bg-gray-800 border-gray-700 text-white"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-gray-300">Additional Information</Label>
                    <div className="relative">
                      <MessageSquare className="absolute left-3 top-3 w-5 h-5 text-gray-500" />
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        placeholder="Tell us about your transportation needs (pickup location, special requirements, etc.)"
                        className="pl-10 min-h-32 bg-gray-800 border-gray-700 text-white placeholder:text-gray-500"
                      />
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    size="lg"
                    className="w-full bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-600 hover:to-purple-700 text-white text-lg py-6"
                  >
                    <Send className="w-5 h-5 mr-2" />
                    Submit Booking Request
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
